import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from '../../hooks/use-toast';
import axios from 'axios';
import { emit, EVT } from '../../services/dataSyncBus';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EditTenantModal = ({ isOpen, onClose, onSuccess, token, tenant }) => {
  const [properties, setProperties] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    property_id: '',
    room_id: '',
    name: '',
    phone: '',
    email: '',
    check_in_date: '',
    rent_amount: '',
    deposit_amount: '',
    status: 'active'
  });

  const fetchProperties = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/properties`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProperties(response.data);
    } catch (error) {
      console.error('Error fetching properties:', error);
    }
  }, [token]);

  const fetchRooms = useCallback(async (propertyId) => {
    try {
      const response = await axios.get(`${API}/properties/${propertyId}/rooms`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRooms(response.data);
    } catch (error) {
      console.error('Error fetching rooms:', error);
      setRooms([]);
    }
  }, [token]);

  useEffect(() => {
    if (isOpen && tenant) {
      setFormData({
        property_id: tenant.property_id || '',
        room_id: tenant.room_id || '',
        name: tenant.name || '',
        phone: tenant.phone || '',
        email: tenant.email || '',
        kyc_type: tenant.kyc_type || '',
        kyc_number: tenant.kyc_number || '',
        check_in_date: tenant.check_in_date || '',
        rent_amount: tenant.rent_amount?.toString() || '',
        deposit_amount: tenant.deposit_amount?.toString() || '',
        status: tenant.status || 'active'
      });
      fetchProperties();
    }
  }, [isOpen, tenant, fetchProperties]);

  useEffect(() => {
    if (formData.property_id) {
      fetchRooms(formData.property_id);
    }
  }, [formData.property_id, fetchRooms]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submitData = {
        ...formData,
        rent_amount: parseFloat(formData.rent_amount),
        deposit_amount: parseFloat(formData.deposit_amount)
      };
      
      
      await axios.put(`${API}/tenants/${tenant.id}`, submitData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      emit(EVT.TENANT, { action: 'updated', id: tenant.id });

      toast({
        title: "Tenant Updated!",
        description: "Tenant details have been updated successfully"
      });

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Tenant update error:', error.response?.data);
      
      // Extract error message from different error formats
      let errorMessage = "Failed to update tenant";
      
      if (error.response?.data) {
        const errorData = error.response.data;
        
        // Handle string detail
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        }
        // Handle validation error array
        else if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map(err => err.msg || err.message).join(', ');
        }
        // Handle validation error object
        else if (errorData.detail && typeof errorData.detail === 'object') {
          errorMessage = errorData.detail.msg || errorData.detail.message || errorMessage;
        }
        // Handle direct message
        else if (errorData.message) {
          errorMessage = errorData.message;
        }
      }
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  if (!tenant) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Tenant</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Property *</Label>
              <select
                value={formData.property_id}
                onChange={(e) => setFormData({...formData, property_id: e.target.value, room_id: ''})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select Property</option>
                {properties.map(prop => (
                  <option key={prop.id} value={prop.id}>{prop.name}</option>
                ))}
              </select>
            </div>

            <div>
              <Label>Room *</Label>
              <select
                value={formData.room_id}
                onChange={(e) => setFormData({...formData, room_id: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
                disabled={!formData.property_id}
              >
                <option value="">Select Room</option>
                {rooms.map(room => (
                  <option key={room.id} value={room.id}>
                    Room {room.room_number}
                    {room.room_type && ` - ${room.room_type}`}
                    {room.rent_per_bed && ` (₹${room.rent_per_bed})`}
                    {room.rent_amount && ` (₹${room.rent_amount}/mo)`}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Label>Tenant Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="John Doe"
                required
              />
            </div>

            <div>
              <Label>Phone Number *</Label>
              <Input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                placeholder="9876543210"
                required
              />
            </div>

            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="john@example.com"
              />
            </div>

            <div>
              <Label>Check-in Date *</Label>
              <Input
                type="date"
                value={formData.check_in_date}
                onChange={(e) => setFormData({...formData, check_in_date: e.target.value})}
                required
              />
            </div>

            <div>
              <Label>KYC Document Type</Label>
              <select
                value={formData.kyc_type}
                onChange={(e) => setFormData({...formData, kyc_type: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Document Type</option>
                <option value="pan_card">PAN Card</option>
                <option value="aadhar_card">Aadhar Card</option>
                <option value="driving_license">Driving License</option>
              </select>
            </div>

            <div>
              <Label>KYC Document Number</Label>
              <Input
                value={formData.kyc_number}
                onChange={(e) => setFormData({...formData, kyc_number: e.target.value})}
                placeholder="Enter document number"
              />
            </div>

            <div>
              <Label>Monthly Rent (₹) *</Label>
              <Input
                type="number"
                value={formData.rent_amount}
                onChange={(e) => setFormData({...formData, rent_amount: e.target.value})}
                placeholder="10000"
                required
                min="0"
              />
            </div>

            <div>
              <Label>Security Deposit (₹) *</Label>
              <Input
                type="number"
                value={formData.deposit_amount}
                onChange={(e) => setFormData({...formData, deposit_amount: e.target.value})}
                placeholder="20000"
                required
                min="0"
              />
            </div>

            <div className="col-span-2">
              <Label>Status *</Label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({...formData, status: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="active">Active</option>
                <option value="notice">Notice Period</option>
                <option value="exited">Exited</option>
              </select>
            </div>
          </div>

          <div className="flex gap-4 pt-4">
            <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700" disabled={loading}>
              {loading ? 'Updating...' : 'Update Tenant'}
            </Button>
            <Button type="button" onClick={onClose} variant="outline" className="flex-1">
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditTenantModal;
